import type { ReactNode } from "react";

import styled from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { DailyRanking, DesktopAndMobile, Info24px, Worldwide } from "@similarweb/icons";
import { colorsPalettes, setElevation } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { Topic, TopicsRawData } from "app/pages/market-research/api-client/demandSearchTrendsTopicsAPI";

import { TopicsHeatmapCategoryList } from "./TopicsHeatmapCategoryList";
import { TopicsHeatmapSearch } from "./TopicsHeatmapSearch";

const Card = styled(Stack)`
    border-radius: 6px;
    overflow: hidden;
    background: ${colorsPalettes.carbon[0]};
    ${setElevation("Z1")}
`;

const Body = styled(Flex)<{ $height: number }>`
    height: ${({ $height }) => $height}px;
`;

const TreemapWrapper = styled.div`
    flex: 1;
    min-width: 0;

    .highcharts-data-label {
        pointer-events: none !important;
    }

    .highcharts-data-label span {
        white-space: normal !important;
        overflow-wrap: break-word !important;
        text-overflow: clip !important;
        overflow: hidden !important;
        text-align: center !important;
    }
`;

type QueryItemProps = {
    icon: ReactNode;
    label: string;
};

const QueryItem = ({ icon, label }: QueryItemProps) => (
    <Flex
        gap="8px"
        alignItems="center"
    >
        {icon}
        <Text
            fontVariant="chip1"
            color={colorsPalettes.carbon[400]}
        >
            {label}
        </Text>
    </Flex>
);

type TopicsHeatmapByCategoryProps = {
    dateRangeLabel: string;
    height: number;
    data: TopicsRawData;
    activeCategory: string;
    onCategorySelect: (categoryName: string) => void;
    flattenedTopics: Topic[];
    onTopicSearchSelect: (topic: Topic) => void;
    children: ReactNode;
};

export const TopicsHeatmapByCategory = ({
    dateRangeLabel,
    height,
    data,
    activeCategory,
    onCategorySelect,
    flattenedTopics,
    onTopicSearchSelect,
    children,
}: TopicsHeatmapByCategoryProps) => {
    const { translate } = useTranslate();

    return (
        <Card data-automation="heatmap-by-category">
            <Stack
                gap="8px"
                paddingX="24px"
                paddingY="20px"
            >
                <Flex
                    gap="8px"
                    alignItems="center"
                >
                    <Text
                        fontVariant="h5_2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {translate("demand_analysis.topics_heatmap.by_category.title")}
                    </Text>
                    <Tooltip
                        content={translate("demand_analysis.topics_heatmap.by_category.tooltip")}
                        placement="top"
                        role="label"
                    >
                        <Info24px
                            size="sm"
                            color={colorsPalettes.carbon[300]}
                        />
                    </Tooltip>
                </Flex>
                <Flex
                    gap="16px"
                    alignItems="center"
                >
                    {dateRangeLabel ? (
                        <QueryItem
                            icon={
                                <DailyRanking
                                    size="sm"
                                    color={colorsPalettes.carbon[300]}
                                />
                            }
                            label={dateRangeLabel}
                        />
                    ) : null}
                    <QueryItem
                        icon={
                            <Worldwide
                                size="sm"
                                color={colorsPalettes.carbon[300]}
                            />
                        }
                        label={translate("demand_analysis.topics_heatmap.by_category.worldwide")}
                    />
                    <QueryItem
                        icon={
                            <DesktopAndMobile
                                size="sm"
                                color={colorsPalettes.carbon[300]}
                            />
                        }
                        label={translate("demand_analysis.topics_heatmap.by_category.all_traffic")}
                    />
                </Flex>
            </Stack>
            <Box
                height="1px"
                width="100%"
                background={colorsPalettes.carbon[100]}
            />
            <TopicsHeatmapSearch
                flattenedTopics={flattenedTopics}
                onTopicSelect={onTopicSearchSelect}
            />
            <Body $height={height}>
                <TopicsHeatmapCategoryList
                    data={data}
                    activeCategory={activeCategory}
                    onCategorySelect={onCategorySelect}
                />
                <TreemapWrapper>{children}</TreemapWrapper>
            </Body>
        </Card>
    );
};
